const DAY_KEYS = {
  MONDAY: 'monday',
  TUESDAY: 'tuesday',
  WEDNESDAY: 'wednesday',
  THURSDAY: 'thursday',
  FRIDAY: 'friday',
  SATURDAY: 'saturday',
  SUNDAY: 'sunday'
};

const HOME_TYPE = 'Home services (plumbing, HVAC, roofing, etc.)';
const OTHER_TYPE =
  'Other local business (restaurant, dentist, auto shop, etc.)';
const OTHER_CATEGORY = 'Other (please describe)';

const HOME_SERVICES = [
  'Air Duct Cleaning',
  'Appliance Repair',
  'Artificial Turf Installation',
  'Bathroom Remodeling',
  'Cabinet Installation & Refinishing',
  'Carpet Cleaning',
  'Chimney Cleaning & Repair',
  'Concrete Contractor',
  'Deck & Patio Contractor',
  'Door Installation & Repair',
  'Drain Cleaning',
  'Dryer Vent Cleaning',
  'Electrician',
  'Fencing Contractor',
  'Flooring Contractor',
  'Foundation Repair',
  'Garage Door Repair & Installation',
  'General Contractor',
  'Generator Installation',
  'Gutter Installation & Cleaning',
  'Handyman',
  'Home Cleaning',
  'Home Inspection',
  'Home Remodeling',
  'Home Security Installation',
  'HVAC (Heating & Air Conditioning)',
  'Insulation Contractor',
  'Irrigation & Sprinkler Services',
  'Junk Removal',
  'Kitchen Remodeling',
  'Landscaping',
  'Lawn Care',
  'Locksmith',
  'Masonry Contractor',
  'Mold Remediation',
  'Moving Company',
  'Painting Contractor',
  'Paver Installation',
  'Pest Control',
  'Plumber',
  'Pool Cleaning & Maintenance',
  'Pool Construction & Remodeling',
  'Pressure Washing',
  'Roofing Contractor',
  'Septic Tank Services',
  'Siding Contractor',
  'Solar Panel Installation',
  'Tree Services',
  'Water Damage Restoration',
  'Water Filtration & Treatment',
  'Waterproofing (Basement & Foundation)',
  'Window Cleaning',
  'Window Installation & Replacement',
  OTHER_CATEGORY
];

const OTHER_INDUSTRIES = [
  'Restaurant',
  'Cafe / Coffee Shop',
  'Bakery',
  'Dentist',
  'Doctor / Clinic',
  'Chiropractor',
  'Hair Salon',
  'Barber',
  'Spa / Massage',
  'Fitness / Gym',
  'Auto Repair',
  'Car Wash',
  OTHER_CATEGORY
];

const formatGoogleTime = (time) => {
  const hour = Number(time?.hours);
  const minute = Number(time?.minutes || 0);
  if (!Number.isInteger(hour) || !Number.isInteger(minute)) return null;

  return `${String(hour % 24).padStart(2, '0')}:${String(minute).padStart(
    2,
    '0'
  )}`;
};

const timeToMinutes = (time) => {
  const [hour, minute] = time.split(':').map(Number);
  return hour * 60 + minute;
};

const toBusinessHours = (periods = []) => {
  const days = Object.values(DAY_KEYS).reduce((result, day) => {
    result[day] = {
      isOpen: false,
      open24: false,
      openTime: '09:00',
      closeTime: '17:00'
    };
    return result;
  }, {});

  periods.forEach((period) => {
    const day = DAY_KEYS[period.openDay];
    const openTime = formatGoogleTime(period.openTime);
    const closeTime = formatGoogleTime(period.closeTime);
    if (!day || !openTime || !closeTime) return;

    const current = days[day];
    const is24Hours =
      openTime === '00:00' &&
      closeTime === '00:00' &&
      period.closeDay === period.openDay;
    days[day] = {
      isOpen: true,
      open24: current.isOpen ? current.open24 || is24Hours : is24Hours,
      openTime:
        current.isOpen &&
        !is24Hours &&
        timeToMinutes(current.openTime) < timeToMinutes(openTime)
          ? current.openTime
          : openTime,
      closeTime:
        current.isOpen &&
        !is24Hours &&
        timeToMinutes(current.closeTime) > timeToMinutes(closeTime)
          ? current.closeTime
          : closeTime
    };
  });

  return { days };
};

const formatAddress = (address) => {
  if (typeof address === 'string') return address;
  if (!address) return '';

  return [
    ...(address.addressLines || []),
    address.locality,
    address.administrativeArea,
    address.postalCode
  ]
    .filter(Boolean)
    .join(', ');
};

const getCategoryName = (location) =>
  location.categories?.primaryCategory?.displayName ||
  location.primaryCategory?.displayName ||
  location.category?.displayName ||
  location.category ||
  '';

const mapBusinessType = (category) => {
  if (!category) return undefined;

  const homeService = HOME_SERVICES.find(
    (service) => service.toLowerCase() === category.toLowerCase()
  );
  if (homeService && homeService !== OTHER_CATEGORY) {
    return { type: HOME_TYPE, choice: homeService };
  }

  const otherIndustry = OTHER_INDUSTRIES.find(
    (industry) => industry.toLowerCase() === category.toLowerCase()
  );
  if (otherIndustry && otherIndustry !== OTHER_CATEGORY) {
    return { type: OTHER_TYPE, choice: otherIndustry };
  }

  return {
    type: OTHER_TYPE,
    choice: OTHER_CATEGORY,
    other: category
  };
};

const getServices = (location) => {
  const serviceItems = location.serviceItems || location.services;
  if (!Array.isArray(serviceItems)) return '';

  return serviceItems
    .map((service) =>
      typeof service === 'string'
        ? service
        : service.label || service.displayName || service.name
    )
    .filter(Boolean)
    .join('\n');
};

export const mapGoogleLocationToAnswers = (location = {}) => {
  const answers = {};
  const name = location.title || location.locationName;
  const phone =
    location.phoneNumber ||
    location.phoneNumbers?.primaryPhone ||
    location.primaryPhone;
  const website = location.websiteUrl || location.websiteUri;
  const address = formatAddress(
    location.address || location.storefrontAddress || location.formattedAddress
  );
  const serviceArea =
    location.serviceArea?.places?.placeInfos ||
    location.serviceArea?.places ||
    location.serviceAreas;
  const areas = Array.isArray(serviceArea)
    ? serviceArea
        .map((place) =>
          typeof place === 'string'
            ? place
            : place.placeName || place.name || place.locality
        )
        .filter(Boolean)
        .join('\n')
    : '';
  const description =
    location.profile?.description || location.description || '';
  const category = getCategoryName(location);
  const services = getServices(location);

  if (name) answers.name = name;
  if (phone) answers.phone = phone;
  if (website) answers.website = { url: website, none: false };
  if (address) answers.address = { street: address, suite: '' };
  if (areas) answers.areas = areas;
  if (description) answers.special = description;

  const businessType = mapBusinessType(category);
  if (businessType) answers.businessType = businessType;
  if (services) answers.services = services;

  if (location.regularHours?.periods) {
    answers.hours = toBusinessHours(location.regularHours.periods);
  }

  return answers;
};

export const canContinueAfterGoogleConnection = (
  isConnected,
  locations,
  selectedLocationKey
) => isConnected && (locations.length === 0 || Boolean(selectedLocationKey));
